import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Plus, Globe, Star, Clock, Sparkles, ExternalLink, Twitter, Send, Copy, Heart, TrendingUp, MessageCircle, X } from 'lucide-react';
import { WLFI_COLORS } from './WLFILayout';
import { getUSD1Tokens, USD1TokenData, supabase, TABLES } from '../../utils/supabase';
import { getRelayTokenPriceEth } from '../../hooks/useRelayPrice';
import TradeModal from '../../components/shared/modals/TradeModal';

// WLFI Token constants
const WLFI_TOKEN_ADDRESS = '0xda5e1988097297dcdc1f90d4dfe7909e847cbef6';
const WLFI_TOTAL_SUPPLY = 100_000_000_000; // 100 billion
const DEFAULT_TOKEN_SUPPLY = 1_000_000_000; // 1 billion (default for factory tokens)

// ============================================
// WLFI EXPLORE PAGE
// Matches mavrk.ink/explore layout with 3-column token sections
// ============================================

// ============ STYLED COMPONENTS ============
const PageContainer = styled.div`
  padding: 0;
  background: transparent;
  min-height: calc(100vh - 180px);
  width: 100%;
`;

// ============ HERO SECTION ============
const HeroSection = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: 20px;
  margin-bottom: 32px;
  
  @media (max-width: 1400px) {
    grid-template-columns: 1fr 1fr;
    
    & > *:first-child {
      grid-column: span 2;
    }
  }
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    
    & > *:first-child {
      grid-column: span 2;
    }
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    
    & > *:first-child {
      grid-column: span 1;
    }
  }
`;

const HeroCard = styled.div<{ $featured?: boolean }>`
  background: ${WLFI_COLORS.cardBackground};
  border: 1px solid ${WLFI_COLORS.goldBorder};
  border-radius: 16px;
  padding: 24px;
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: pointer;
  height: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  
  &:hover {
    border-color: ${WLFI_COLORS.goldBorderHover};
    box-shadow: 0 8px 32px rgba(212, 175, 55, 0.15);
  }
  
  @media (max-width: 1400px) {
    height: auto;
    min-height: 320px;
    padding: 20px;
  }
  
  @media (max-width: 600px) {
    min-height: 280px;
    padding: 16px;
  }
`;

const HeroCardBadge = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(212, 175, 55, 0.2);
  border: 1px solid ${WLFI_COLORS.goldBorder};
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  color: ${WLFI_COLORS.gold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

// ============ WLFI FEATURED CARD ============
const WLFIFeaturedContainer = styled.div`
  display: flex;
  gap: 24px;
  height: 100%;
  width: 100%;
  
  @media (max-width: 1400px) {
    gap: 20px;
  }
  
  @media (max-width: 900px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const WLFILogoWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WLFILogo = styled.img`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  object-fit: cover;
  background: ${WLFI_COLORS.cardBackground};
  border: 3px solid ${WLFI_COLORS.goldBorder};
  
  @media (max-width: 1400px) {
    width: 160px;
    height: 160px;
  }
  
  @media (max-width: 900px) {
    width: 120px;
    height: 120px;
  }
  
  @media (max-width: 600px) {
    width: 100px;
    height: 100px;
  }
`;

const WLFIContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding-top: 24px;
  
  @media (max-width: 1400px) {
    padding-top: 16px;
  }
  
  @media (max-width: 900px) {
    padding-top: 12px;
    align-items: center;
  }
`;

const WLFITopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
  
  @media (max-width: 900px) {
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
`;

const WLFINameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  
  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const WLFIName = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  
  @media (max-width: 1400px) {
    font-size: 24px;
  }
  
  @media (max-width: 900px) {
    font-size: 20px;
  }
  
  @media (max-width: 600px) {
    font-size: 18px;
  }
`;

const WLFITradeButton = styled.button`
  padding: 10px 24px;
  background: ${WLFI_COLORS.accentGradient};
  border: none;
  border-radius: 8px;
  color: ${WLFI_COLORS.navy};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.5px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(212, 175, 55, 0.4);
  }
`;

const WLFIMarketCap = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${WLFI_COLORS.lightBlue};
  white-space: nowrap;
  padding: 4px 0;
`;

const WLFISymbolRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const WLFISymbol = styled.span`
  font-size: 15px;
  color: ${WLFI_COLORS.gold};
  font-weight: 600;
`;

const WLFIAddress = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
`;

const WLFIDescription = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin: 0 0 16px 0;
  flex: 1;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
  
  @media (max-width: 1400px) {
    font-size: 13px;
    margin-bottom: 12px;
  }
  
  @media (max-width: 900px) {
    font-size: 12px;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  @media (max-width: 600px) {
    -webkit-line-clamp: 2;
  }
`;

const WLFISocials = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: auto;
  flex-wrap: wrap;
  
  @media (max-width: 900px) {
    justify-content: center;
    gap: 8px;
  }
`;

const SocialIcon = styled.a`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(212, 175, 55, 0.1);
  border: 1px solid ${WLFI_COLORS.goldBorderLight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${WLFI_COLORS.gold};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(212, 175, 55, 0.2);
    border-color: ${WLFI_COLORS.goldBorder};
    color: #ffffff;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  @media (max-width: 900px) {
    width: 32px;
    height: 32px;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

// ============ USD1 CARD ============
const USD1Card = styled(HeroCard)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 28px;
`;

const USD1Logo = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 20px;
  border: 3px solid ${WLFI_COLORS.goldBorder};
  background: ${WLFI_COLORS.cardBackground};
  
  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const USD1Title = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 6px 0;
`;

const USD1Subtitle = styled.div`
  font-size: 13px;
  color: ${WLFI_COLORS.gold};
  margin-bottom: 12px;
  font-weight: 600;
`;

const USD1Description = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.5;
  margin-bottom: 20px;
  max-width: 280px;
`;

const USD1Stats = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
`;

const USD1Stat = styled.div`
  text-align: center;
`;

const USD1StatValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${WLFI_COLORS.lightBlue};
`;

const USD1StatLabel = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const USD1Button = styled.button`
  padding: 12px 28px;
  background: rgba(212, 175, 55, 0.2);
  border: 1px solid ${WLFI_COLORS.goldBorder};
  border-radius: 8px;
  color: ${WLFI_COLORS.gold};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(212, 175, 55, 0.3);
    border-color: ${WLFI_COLORS.goldBorderHover};
    transform: translateY(-1px);
  }
`;

const USD1PriceDisplay = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${WLFI_COLORS.lightBlue};
  margin: 20px 0;
  text-align: center;
`;

// ============ USA TOKEN CARD ============
const USACard = styled(HeroCard)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 28px;
`;

const USALogoClickable = styled.div`
  position: relative;
  cursor: pointer;
  margin-bottom: 20px;
  
  &:hover img {
    border-color: ${WLFI_COLORS.gold};
    transform: scale(1.05);
  }
  
  &:hover span {
    opacity: 1;
  }
`;

const USALogo = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid ${WLFI_COLORS.goldBorder};
  background: ${WLFI_COLORS.cardBackground};
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const USALogoHint = styled.span`
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  color: ${WLFI_COLORS.gold};
  opacity: 0;
  transition: opacity 0.2s ease;
  white-space: nowrap;
`;

const USATitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 6px 0;
`;

const USASymbol = styled.div`
  font-size: 14px;
  color: ${WLFI_COLORS.gold};
  margin-bottom: 12px;
  font-weight: 600;
`;

const USAStats = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
`;

const USAStat = styled.div`
  text-align: center;
`;

const USAStatValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${WLFI_COLORS.lightBlue};
`;

const USAStatLabel = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const USAButton = styled.button`
  padding: 12px 28px;
  background: rgba(212, 175, 55, 0.2);
  border: 1px solid ${WLFI_COLORS.goldBorder};
  border-radius: 8px;
  color: ${WLFI_COLORS.gold};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(212, 175, 55, 0.3);
    border-color: ${WLFI_COLORS.goldBorderHover};
    transform: translateY(-1px);
  }
`;

// ============ TOKEN DETAIL MODAL ============
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: ${WLFI_COLORS.cardBackground};
  border: 1px solid ${WLFI_COLORS.goldBorder};
  border-radius: 16px;
  padding: 32px;
  max-width: 450px;
  width: 100%;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalCloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalLogo = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid ${WLFI_COLORS.goldBorder};
  background: ${WLFI_COLORS.cardBackground};
  margin-bottom: 16px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 4px 0;
  text-align: center;
`;

const ModalSymbol = styled.div`
  font-size: 14px;
  color: ${WLFI_COLORS.gold};
  font-weight: 600;
`;

const ModalDescription = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin: 0 0 24px 0;
  text-align: center;
`;

const ModalStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-bottom: 24px;
  padding: 16px;
  background: rgba(212, 175, 55, 0.1);
  border-radius: 12px;
  border: 1px solid ${WLFI_COLORS.goldBorderLight};
`;

const ModalStat = styled.div`
  text-align: center;
`;

const ModalStatValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${WLFI_COLORS.lightBlue};
`;

const ModalStatLabel = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
`;

const ModalContract = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 24px;
`;

const ModalContractAddress = styled.code`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  font-family: monospace;
`;

const ModalCopyButton = styled.button`
  padding: 6px 12px;
  background: rgba(212, 175, 55, 0.2);
  border: 1px solid ${WLFI_COLORS.goldBorder};
  border-radius: 6px;
  color: ${WLFI_COLORS.gold};
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.15s ease;
  
  &:hover {
    background: rgba(212, 175, 55, 0.3);
  }
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const ModalSocials = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const ModalSocialIcon = styled.a`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(212, 175, 55, 0.1);
  border: 1px solid ${WLFI_COLORS.goldBorderLight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${WLFI_COLORS.gold};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(212, 175, 55, 0.2);
    border-color: ${WLFI_COLORS.goldBorder};
    color: #ffffff;
    transform: translateY(-2px);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const ModalTradeButton = styled.button`
  width: 100%;
  padding: 14px 28px;
  background: ${WLFI_COLORS.accentGradient};
  border: none;
  border-radius: 10px;
  color: ${WLFI_COLORS.navy};
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(212, 175, 55, 0.3);
  }
`;

// ============ CREATE TOKEN CARD ============
const CreateCard = styled(HeroCard)`
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const CreateIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(212, 175, 55, 0.2);
  border: 2px solid ${WLFI_COLORS.goldBorder};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  
  svg {
    width: 28px;
    height: 28px;
    color: ${WLFI_COLORS.gold};
  }
`;

const CreateTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 8px 0;
`;

const CreateSubtitle = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 20px;
  max-width: 220px;
`;

const CreateButton = styled.button`
  padding: 12px 32px;
  background: ${WLFI_COLORS.accentGradient};
  border: none;
  border-radius: 8px;
  color: ${WLFI_COLORS.navy};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(212, 175, 55, 0.4);
  }
`;

// ============ 3-COLUMN TOKEN LIST SECTIONS ============
const ListSectionContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ListSection = styled.div`
  background: ${WLFI_COLORS.cardBackground};
  border: 1px solid ${WLFI_COLORS.goldBorder};
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 485px;
  
  @media (max-width: 768px) {
    height: auto;
    min-height: 300px;
  }
`;

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${WLFI_COLORS.goldBorderLight};
`;

const ListTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  
  svg {
    width: 16px;
    height: 16px;
    color: ${WLFI_COLORS.gold};
  }
`;

const ListExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(212, 175, 55, 0.1);
  border: 1px solid ${WLFI_COLORS.goldBorderLight};
  border-radius: 6px;
  color: ${WLFI_COLORS.gold};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(212, 175, 55, 0.2);
    color: #ffffff;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ListContent = styled.div`
  flex: 1;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${WLFI_COLORS.goldBorderLight};
    border-radius: 2px;
  }
`;

const TokenRow = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 16px;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(212, 175, 55, 0.1);
  
  &:hover {
    background: rgba(212, 175, 55, 0.1);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TokenLogo = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  object-fit: cover;
  background: ${WLFI_COLORS.cardBackground};
  border: 1px solid ${WLFI_COLORS.goldBorderLight};
  flex-shrink: 0;
`;

const TokenInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TokenNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TokenName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TokenSymbol = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
`;

const TokenAddress = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  background: rgba(212, 175, 55, 0.1);
  border: 1px solid ${WLFI_COLORS.goldBorderLight};
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 10px;
  font-family: monospace;
  cursor: pointer;
  transition: all 0.15s ease;
  
  svg {
    width: 10px;
    height: 10px;
  }
  
  &:hover {
    background: rgba(212, 175, 55, 0.2);
    border-color: ${WLFI_COLORS.goldBorder};
    color: ${WLFI_COLORS.gold};
  }
`;

const TokenStats = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
`;

const TokenMarketCap = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${WLFI_COLORS.gold};
`;

const TokenPrice = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
`;

const PriceChange = styled.div<{ $positive?: boolean }>`
  font-size: 10px;
  font-weight: 500;
  color: ${props => props.$positive !== false ? '#10b981' : '#ef4444'};
`;

const TokenActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const TokenTradeButton = styled.button`
  padding: 5px 10px;
  background: ${WLFI_COLORS.accentGradient};
  border: none;
  border-radius: 6px;
  color: ${WLFI_COLORS.navy};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(212, 175, 55, 0.4);
  }
`;

const TokenFavoriteButton = styled.button<{ $isFavorited?: boolean }>`
  width: 28px;
  height: 28px;
  padding: 0;
  background: ${props => props.$isFavorited ? 'rgba(212, 175, 55, 0.2)' : 'transparent'};
  border: 1px solid ${props => props.$isFavorited ? WLFI_COLORS.goldBorder : WLFI_COLORS.goldBorderLight};
  border-radius: 6px;
  color: ${props => props.$isFavorited ? WLFI_COLORS.gold : 'rgba(255, 255, 255, 0.4)'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(212, 175, 55, 0.15);
    border-color: ${WLFI_COLORS.goldBorder};
    color: ${WLFI_COLORS.gold};
  }
  
  svg {
    width: 14px;
    height: 14px;
    fill: ${props => props.$isFavorited ? WLFI_COLORS.gold : 'none'};
  }
`;

const TokenFavorites = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${WLFI_COLORS.gold};
  
  svg {
    width: 12px;
    height: 12px;
    fill: ${WLFI_COLORS.gold};
  }
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
`;

// ============ SCROLLING FEED ============
const scroll = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const ScrollingFeedContainer = styled.div`
  width: 100%;
  overflow: hidden;
  background: ${WLFI_COLORS.cardBackground};
  border: 1px solid ${WLFI_COLORS.goldBorder};
  border-radius: 16px;
  padding: 16px 0;
  margin-top: 24px;
`;

const ScrollingFeedTrack = styled.div`
  display: flex;
  animation: ${scroll} 20s linear infinite;
  width: fit-content;
  
  &:hover {
    animation-play-state: paused;
  }
`;

const ScrollingTokenCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  margin: 0 8px;
  background: rgba(212, 175, 55, 0.1);
  border: 1px solid ${WLFI_COLORS.goldBorderLight};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    background: rgba(212, 175, 55, 0.2);
    border-color: ${WLFI_COLORS.goldBorder};
    transform: translateY(-2px);
  }
`;

const ScrollingTokenLogo = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  background: ${WLFI_COLORS.cardBackground};
`;

const ScrollingTokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ScrollingTokenSymbol = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
`;

const ScrollingTokenCategory = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: capitalize;
`;

const ScrollingTokenMarketCap = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${WLFI_COLORS.lightBlue};
  margin-left: auto;
`;

// ============ COMPONENT ============
const WLFIExplorePage: React.FC = () => {
  const navigate = useNavigate();
  
  // Data states
  const [recentlyDeployed, setRecentlyDeployed] = useState<USD1TokenData[]>([]);
  const [featuredTokens, setFeaturedTokens] = useState<USD1TokenData[]>([]);
  const [communityFavorites, setCommunityFavorites] = useState<USD1TokenData[]>([]);
  const [scrollingTokens, setScrollingTokens] = useState<USD1TokenData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // WLFI token price state
  const [wlfiPrice, setWlfiPrice] = useState<number | null>(null);
  const [wlfiMarketCap, setWlfiMarketCap] = useState<string>('$2.5B');
  
  // Token prices cache
  const [tokenPrices, setTokenPrices] = useState<Map<string, number>>(new Map());
  
  // Local favorites state
  const [localFavorites, setLocalFavorites] = useState<Set<string>>(new Set());
  
  // Trade modal state
  const [tradeModalOpen, setTradeModalOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState<{
    address: string;
    symbol: string;
    name: string;
    chainId: number;
    logo: string | null;
    decimals: number;
  } | null>(null);
  
  // USA token state (fetched from tokens_usd1 table)
  const [usaToken, setUsaToken] = useState<USD1TokenData | null>(null);
  const [usaTokenPrice, setUsaTokenPrice] = useState<number | null>(null);
  const [usaMarketCap, setUsaMarketCap] = useState<string>('--');
  const [usaModalOpen, setUsaModalOpen] = useState(false);
  
  // USA token contract address
  const USA_TOKEN_ADDRESS = '0xa3941fa16b982bb458694868dedd8db669203346';
  
  // Fetch USA token from Supabase
  useEffect(() => {
    const fetchUSAToken = async () => {
      try {
        const { data, error } = await supabase
          .from(TABLES.TOKENS_USD1)
          .select('*')
          .ilike('contract_address', USA_TOKEN_ADDRESS)
          .single();
        
        if (data && !error) {
          setUsaToken(data);
          console.log('[WLFI] Loaded USA token:', data);
          
          // Fetch price
          const price = await getRelayTokenPriceEth(USA_TOKEN_ADDRESS);
          if (price) {
            setUsaTokenPrice(price);
            const marketCap = price * DEFAULT_TOKEN_SUPPLY;
            setUsaMarketCap(formatMarketCap(marketCap));
            console.log(`[WLFI] USA Price: $${price}, Market Cap: ${formatMarketCap(marketCap)}`);
          }
        }
      } catch (err) {
        console.error('[WLFI] Error fetching USA token:', err);
      }
    };
    
    fetchUSAToken();
  }, []);
  
  // Fetch WLFI token price
  useEffect(() => {
    const fetchWLFIPrice = async () => {
      try {
        console.log('[WLFI] Fetching WLFI token price...');
        const price = await getRelayTokenPriceEth(WLFI_TOKEN_ADDRESS);
        if (price) {
          setWlfiPrice(price);
          const marketCap = price * WLFI_TOTAL_SUPPLY;
          setWlfiMarketCap(formatMarketCap(marketCap));
          console.log(`[WLFI] Price: $${price}, Market Cap: ${formatMarketCap(marketCap)}`);
        }
      } catch (error) {
        console.error('[WLFI] Error fetching WLFI price:', error);
      }
    };
    
    fetchWLFIPrice();
    // Refresh every 30 seconds
    const interval = setInterval(fetchWLFIPrice, 30000);
    return () => clearInterval(interval);
  }, []);
  
  // Fetch all token data
  useEffect(() => {
    const fetchTokens = async () => {
      setIsLoading(true);
      try {
        // Fetch recently deployed (by created_at)
        const recent = await getUSD1Tokens({
          orderBy: 'created_at',
          ascending: false,
          limit: 20
        });
        setRecentlyDeployed(recent);
        
        // For featured, use the same list for now
        setFeaturedTokens(recent.slice(0, 10));
        
        // Fetch by favorites_count for community favorites
        const { data: favorites, error } = await supabase
          .from(TABLES.TOKENS_USD1)
          .select('*')
          .order('favorites_count', { ascending: false })
          .limit(20);
        
        if (!error && favorites) {
          setCommunityFavorites(favorites);
        }
        
        // For scrolling feed - include all tokens, duplicate only if we have enough
        const scrollTokens = recent.length > 0 ? recent : [];
        // Only duplicate if we have more than 3 tokens to avoid obvious repetition
        if (scrollTokens.length >= 3) {
          setScrollingTokens([...scrollTokens, ...scrollTokens]);
        } else if (scrollTokens.length > 0) {
          // For few tokens, repeat more times for smooth scrolling
          setScrollingTokens([...scrollTokens, ...scrollTokens, ...scrollTokens, ...scrollTokens]);
        } else {
          setScrollingTokens([]);
        }
        
        // Fetch prices for all tokens
        const allTokens = [...new Set([...recent, ...(favorites || [])])];
        const pricesMap = new Map<string, number>();
        
        for (const token of allTokens) {
          if (token.contract_address) {
            try {
              const price = await getRelayTokenPriceEth(token.contract_address);
              if (price) {
                pricesMap.set(token.contract_address.toLowerCase(), price);
              }
            } catch (e) {
              console.warn(`[WLFI] Failed to fetch price for ${token.symbol}`);
            }
          }
        }
        
        setTokenPrices(pricesMap);
        
      } catch (error) {
        console.error('Error fetching tokens:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTokens();
  }, []);
  
  // Format market cap
  const formatMarketCap = (value: number): string => {
    if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(2)}K`;
    return `$${value.toFixed(2)}`;
  };
  
  // Format price with subscript notation for small values
  const formatPrice = (price: number | null): string => {
    if (!price) return '--';
    if (price >= 0.01) return `$${price.toFixed(4)}`;
    if (price >= 0.0001) return `$${price.toFixed(6)}`;
    
    // For very small prices, use subscript notation
    const priceStr = price.toFixed(20);
    const match = priceStr.match(/^0\.0*([1-9])/);
    if (match) {
      const zeros = priceStr.indexOf(match[1]) - 2;
      const significantDigits = priceStr.slice(priceStr.indexOf(match[1]), priceStr.indexOf(match[1]) + 2);
      return `$0.0₍${zeros}₎${significantDigits}`;
    }
    return `$${price.toFixed(8)}`;
  };
  
  // Get market cap for a token
  const getTokenMarketCap = (token: USD1TokenData): string => {
    const price = tokenPrices.get(token.contract_address?.toLowerCase() || '');
    if (!price) return '--';
    const marketCap = price * DEFAULT_TOKEN_SUPPLY;
    return formatMarketCap(marketCap);
  };
  
  // Get price for a token
  const getTokenPrice = (token: USD1TokenData): string => {
    const price = tokenPrices.get(token.contract_address?.toLowerCase() || '');
    return formatPrice(price ?? null);
  };
  
  // Format time ago
  const formatTimeAgo = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays}d`;
    if (diffHours > 0) return `${diffHours}h`;
    if (diffMins > 0) return `${diffMins}m`;
    return 'now';
  };
  
  // Handle token click - open Dextools page
  const handleTokenClick = (token: USD1TokenData) => {
    // Use pool address if available, otherwise use token address
    const dextoolsUrl = token.pool_address 
      ? `https://www.dextools.io/app/en/ether/pair-explorer/${token.pool_address}`
      : `https://www.dextools.io/app/en/ether/token/${token.contract_address}`;
    window.open(dextoolsUrl, '_blank');
  };
  
  // Handle trade button - open swap modal
  const handleTrade = (token: USD1TokenData, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedToken({
      address: token.contract_address || '',
      symbol: token.symbol,
      name: token.name,
      chainId: 1, // Ethereum mainnet
      logo: token.logo_url || null,
      decimals: 18,
    });
    setTradeModalOpen(true);
  };
  
  // Handle favorite toggle
  const handleToggleFavorite = async (tokenId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalFavorites(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tokenId)) {
        newSet.delete(tokenId);
      } else {
        newSet.add(tokenId);
      }
      return newSet;
    });
  };
  
  // WLFI Token info
  const wlfiToken = {
    name: 'World Liberty Financial',
    symbol: 'WLFI',
    address: WLFI_TOKEN_ADDRESS,
    description: 'World Liberty Financial is a governance token that empowers holders to participate in the future of decentralized finance. Built on Ethereum, WLFI represents a new era of financial freedom and transparency.',
    marketCap: wlfiMarketCap,
    website: 'https://worldlibertyfinancial.com',
    twitter: 'https://twitter.com/worldlibertyfi',
  };
  
  // Render token row
  const handleCopyAddress = (address: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(address);
    // Could add a toast notification here
  };
  
  const renderTokenRow = (token: USD1TokenData, showFavoritesCount: boolean = false) => {
    const marketCap = getTokenMarketCap(token);
    const price = getTokenPrice(token);
    const shortAddress = token.contract_address 
      ? `${token.contract_address.slice(0, 6)}...${token.contract_address.slice(-4)}`
      : '';
    
    return (
      <TokenRow key={token.id || token.contract_address} onClick={() => handleTokenClick(token)}>
        <TokenLogo 
          src={token.logo_url || '/token-placeholder.png'} 
          alt={token.name}
          onError={(e) => { (e.target as HTMLImageElement).src = '/token-placeholder.png'; }}
        />
        <TokenInfo>
          <TokenNameRow>
            <TokenName>{token.name}</TokenName>
            {token.contract_address && (
              <TokenAddress onClick={(e) => handleCopyAddress(token.contract_address!, e)}>
                {shortAddress}
                <Copy />
              </TokenAddress>
            )}
          </TokenNameRow>
          <TokenSymbol>${token.symbol}</TokenSymbol>
        </TokenInfo>
        <TokenStats>
          <TokenMarketCap>{marketCap}</TokenMarketCap>
          <TokenPrice>{price}</TokenPrice>
          <PriceChange $positive={true}>+0.00%</PriceChange>
        </TokenStats>
        <TokenActions>
          {showFavoritesCount && (
            <TokenFavorites>
              <Heart />
              {token.favorites_count || 0}
            </TokenFavorites>
          )}
          <TokenTradeButton onClick={(e) => handleTrade(token, e)}>
            Trade
          </TokenTradeButton>
          <TokenFavoriteButton 
            $isFavorited={localFavorites.has(token.id || '')}
            onClick={(e) => handleToggleFavorite(token.id || '', e)}
          >
            <Star />
          </TokenFavoriteButton>
        </TokenActions>
      </TokenRow>
    );
  };
  
  return (
    <PageContainer>
      {/* ============ HERO SECTION ============ */}
      <HeroSection>
        {/* WLFI Featured Card */}
        <HeroCard $featured>
          <HeroCardBadge>
            <Star /> Featured Token
          </HeroCardBadge>
          <WLFIFeaturedContainer>
            <WLFILogoWrapper>
              <WLFILogo 
                src="/wlfi_logo.png" 
                alt="WLFI Token"
                onError={(e) => { (e.target as HTMLImageElement).src = '/token-placeholder.png'; }}
              />
            </WLFILogoWrapper>
            
            <WLFIContent>
              <WLFITopRow>
                <WLFINameRow>
                  <WLFIName>{wlfiToken.name}</WLFIName>
                  <WLFITradeButton onClick={() => navigate('/app/wlfi/trade')}>
                    TRADE
                  </WLFITradeButton>
                </WLFINameRow>
                <WLFIMarketCap>
                  MC: {wlfiToken.marketCap}
                </WLFIMarketCap>
              </WLFITopRow>
              
              <WLFISymbolRow>
                <WLFISymbol>${wlfiToken.symbol}</WLFISymbol>
                <WLFIAddress>
                  {wlfiToken.address.slice(0, 6)}...{wlfiToken.address.slice(-4)}
                </WLFIAddress>
              </WLFISymbolRow>
              
              <WLFIDescription>
                {wlfiToken.description}
              </WLFIDescription>
              
              <WLFISocials>
                <SocialIcon onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(wlfiToken.address);
                }}>
                  <Copy />
                </SocialIcon>
                <SocialIcon href={wlfiToken.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                  <Globe />
                </SocialIcon>
                <SocialIcon href={wlfiToken.twitter} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                  <Twitter />
                </SocialIcon>
                <SocialIcon href={`https://etherscan.io/token/${wlfiToken.address}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                  <ExternalLink />
                </SocialIcon>
              </WLFISocials>
            </WLFIContent>
          </WLFIFeaturedContainer>
        </HeroCard>
        
        {/* USD1 Stablecoin Card */}
        <USD1Card>
          <HeroCardBadge>
            <TrendingUp /> Stablecoin
          </HeroCardBadge>
          <USD1Logo src="/usd1_logo.png" alt="USD1" />
          <USD1Title>USD1 Stablecoin</USD1Title>
          <USD1Subtitle>World Liberty Financial</USD1Subtitle>
          <USD1PriceDisplay>$1.00</USD1PriceDisplay>
          <USD1Button onClick={() => window.open('https://etherscan.io/token/0x8d0d000ee44948fc98c9b98a4fa4921476f08b0d', '_blank')}>
            View USD1
          </USD1Button>
        </USD1Card>
        
        {/* USA Token Card */}
        <USACard>
          <HeroCardBadge>
            <TrendingUp /> Launchpad Token
          </HeroCardBadge>
          <USALogoClickable onClick={() => setUsaModalOpen(true)}>
            {usaToken?.logo_url ? (
              <USALogo src={usaToken.logo_url} alt="USA Token" onError={(e) => { (e.target as HTMLImageElement).src = '/token-placeholder.png'; }} />
            ) : (
              <USALogo src="/token-placeholder.png" alt="USA Token" />
            )}
            <USALogoHint>Click for details</USALogoHint>
          </USALogoClickable>
          <USATitle>{usaToken?.name || 'USA Token'}</USATitle>
          <USASymbol>${usaToken?.symbol || 'USA'}</USASymbol>
          <USAStats>
            <USAStat>
              <USAStatValue>{usaTokenPrice ? `$${usaTokenPrice.toFixed(8)}` : '--'}</USAStatValue>
              <USAStatLabel>Price</USAStatLabel>
            </USAStat>
            <USAStat>
              <USAStatValue>{usaMarketCap}</USAStatValue>
              <USAStatLabel>Market Cap</USAStatLabel>
            </USAStat>
          </USAStats>
          <USAButton onClick={() => {
            if (usaToken) {
              setSelectedToken({
                address: usaToken.contract_address || '',
                symbol: usaToken.symbol,
                name: usaToken.name,
                chainId: 1,
                logo: usaToken.logo_url || null,
                decimals: 18,
              });
              setTradeModalOpen(true);
            }
          }}>
            Trade USA
          </USAButton>
        </USACard>
        
        {/* Create Token Card */}
        <CreateCard onClick={() => navigate('/app/wlfi/create')}>
          <CreateIcon>
            <Plus />
          </CreateIcon>
          <CreateTitle>Create Token</CreateTitle>
          <CreateSubtitle>
            Launch your token on Ethereum paired with USD1 liquidity
          </CreateSubtitle>
          <CreateButton>Get Started</CreateButton>
        </CreateCard>
      </HeroSection>
      
      {/* ============ 3-COLUMN TOKEN SECTIONS ============ */}
      <ListSectionContainer>
        {/* Recently Deployed */}
        <ListSection>
          <ListHeader>
            <ListTitle>
              <Clock /> Recently Deployed
            </ListTitle>
            <ListExpandButton onClick={() => navigate('/app/wlfi/trade')}>
              <ChevronRight />
            </ListExpandButton>
          </ListHeader>
          <ListContent>
            {isLoading ? (
              <EmptyState>Loading...</EmptyState>
            ) : recentlyDeployed.length > 0 ? (
              recentlyDeployed.slice(0, 5).map(token => renderTokenRow(token))
            ) : (
              <EmptyState>No tokens deployed yet</EmptyState>
            )}
          </ListContent>
        </ListSection>
        
        {/* Featured */}
        <ListSection>
          <ListHeader>
            <ListTitle>
              <Sparkles /> Featured
            </ListTitle>
            <ListExpandButton onClick={() => navigate('/app/wlfi/trade')}>
              <ChevronRight />
            </ListExpandButton>
          </ListHeader>
          <ListContent>
            {isLoading ? (
              <EmptyState>Loading...</EmptyState>
            ) : featuredTokens.length > 0 ? (
              featuredTokens.slice(0, 5).map(token => renderTokenRow(token))
            ) : (
              <EmptyState>No featured tokens yet</EmptyState>
            )}
          </ListContent>
        </ListSection>
        
        {/* Community Favorites */}
        <ListSection>
          <ListHeader>
            <ListTitle>
              <Heart /> Community Favorites
            </ListTitle>
            <ListExpandButton onClick={() => navigate('/app/wlfi/trade')}>
              <ChevronRight />
            </ListExpandButton>
          </ListHeader>
          <ListContent>
            {isLoading ? (
              <EmptyState>Loading...</EmptyState>
            ) : communityFavorites.length > 0 ? (
              communityFavorites.slice(0, 5).map(token => renderTokenRow(token, true))
            ) : (
              <EmptyState>No favorites yet</EmptyState>
            )}
          </ListContent>
        </ListSection>
      </ListSectionContainer>
      
      {/* ============ SCROLLING TOKEN FEED ============ */}
      {scrollingTokens.length > 0 && (
        <ScrollingFeedContainer>
          <ScrollingFeedTrack>
            {scrollingTokens.map((token, index) => (
              <ScrollingTokenCard 
                key={`${token.id}-${index}`}
                onClick={() => handleTokenClick(token)}
              >
                <ScrollingTokenLogo 
                  src={token.logo_url || '/token-placeholder.png'} 
                  alt={token.symbol}
                  onError={(e) => { (e.target as HTMLImageElement).src = '/token-placeholder.png'; }}
                />
                <ScrollingTokenInfo>
                  <ScrollingTokenSymbol>${token.symbol}</ScrollingTokenSymbol>
                  <ScrollingTokenCategory>{token.category || 'Token'}</ScrollingTokenCategory>
                </ScrollingTokenInfo>
                <ScrollingTokenMarketCap>{getTokenMarketCap(token)}</ScrollingTokenMarketCap>
              </ScrollingTokenCard>
            ))}
          </ScrollingFeedTrack>
        </ScrollingFeedContainer>
      )}
      
      {/* USA Token Detail Modal */}
      {usaModalOpen && usaToken && (
        <ModalOverlay onClick={() => setUsaModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalCloseButton onClick={() => setUsaModalOpen(false)}>
              <X />
            </ModalCloseButton>
            
            <ModalHeader>
              <ModalLogo 
                src={usaToken.logo_url || '/token-placeholder.png'} 
                alt={usaToken.name}
                onError={(e) => { (e.target as HTMLImageElement).src = '/token-placeholder.png'; }}
              />
              <ModalTitle>{usaToken.name}</ModalTitle>
              <ModalSymbol>${usaToken.symbol}</ModalSymbol>
            </ModalHeader>
            
            {usaToken.description && (
              <ModalDescription>{usaToken.description}</ModalDescription>
            )}
            
            <ModalStats>
              <ModalStat>
                <ModalStatValue>{usaTokenPrice ? `$${usaTokenPrice.toFixed(8)}` : '--'}</ModalStatValue>
                <ModalStatLabel>Price</ModalStatLabel>
              </ModalStat>
              <ModalStat>
                <ModalStatValue>{usaMarketCap}</ModalStatValue>
                <ModalStatLabel>Market Cap</ModalStatLabel>
              </ModalStat>
            </ModalStats>
            
            {usaToken.contract_address && (
              <ModalContract>
                <ModalContractAddress>
                  {usaToken.contract_address.slice(0, 10)}...{usaToken.contract_address.slice(-8)}
                </ModalContractAddress>
                <ModalCopyButton onClick={() => navigator.clipboard.writeText(usaToken.contract_address!)}>
                  <Copy /> Copy
                </ModalCopyButton>
              </ModalContract>
            )}
            
            <ModalSocials>
              {usaToken.website && (
                <ModalSocialIcon href={usaToken.website} target="_blank" rel="noopener noreferrer" title="Website">
                  <Globe />
                </ModalSocialIcon>
              )}
              {usaToken.twitter && (
                <ModalSocialIcon href={usaToken.twitter} target="_blank" rel="noopener noreferrer" title="Twitter">
                  <Twitter />
                </ModalSocialIcon>
              )}
              {usaToken.telegram && (
                <ModalSocialIcon href={usaToken.telegram} target="_blank" rel="noopener noreferrer" title="Telegram">
                  <MessageCircle />
                </ModalSocialIcon>
              )}
              {usaToken.contract_address && (
                <ModalSocialIcon href={`https://etherscan.io/token/${usaToken.contract_address}`} target="_blank" rel="noopener noreferrer" title="Etherscan">
                  <ExternalLink />
                </ModalSocialIcon>
              )}
            </ModalSocials>
            
            <ModalTradeButton onClick={() => {
              setUsaModalOpen(false);
              setSelectedToken({
                address: usaToken.contract_address || '',
                symbol: usaToken.symbol,
                name: usaToken.name,
                chainId: 1,
                logo: usaToken.logo_url || null,
                decimals: 18,
              });
              setTradeModalOpen(true);
            }}>
              Trade {usaToken.symbol}
            </ModalTradeButton>
          </ModalContent>
        </ModalOverlay>
      )}
      
      {/* Trade Modal */}
      <TradeModal
        isOpen={tradeModalOpen}
        onClose={() => {
          setTradeModalOpen(false);
          setSelectedToken(null);
        }}
        token={selectedToken}
      />
    </PageContainer>
  );
};

export default WLFIExplorePage;
